// src/helpers/pdfRemitoCampo.js
import { jsPDF } from "jspdf";
import { getRemitoById } from "./rutaRemitos";
import {
  fmtNum,
  S,
  loadImageAsDataURL,
  drawHeader,
} from "./pdfCommonSanPancracio";

import campoImg from "../images/campocortada.jpeg";

/* ------------------------------ helpers ------------------------------ */

function wrapN(doc, txt, maxW, maxLines = 2) {
  const lines = doc.splitTextToSize(S(txt || "-"), maxW) || [];
  if (lines.length <= maxLines) return lines;

  const out = lines.slice(0, maxLines);
  let last = out[maxLines - 1];

  while (last.length > 0 && doc.getTextWidth(last + "…") > maxW) {
    last = last.slice(0, -1);
  }

  out[maxLines - 1] = (last ? last : " ") + "…";
  return out;
}

function cleanPhone(v) {
  const s = String(v ?? "").replace(/\s+/g, " ").trim();
  if (!s || s === "-" || s === "0") return "";

  const digits = s.replace(/\D/g, "");
  if (digits.length < 6) return "";

  return s;
}

function pickPhone(...objs) {
  const keys = [
    "telefono",
    "tel",
    "celular",
    "movil",
    "whatsapp",
    "wsp",
    "phone",
  ];

  for (const o of objs) {
    if (!o) continue;

    const direct = cleanPhone(o);
    if (direct && direct !== "[object Object]") return direct;

    for (const k of keys) {
      const val = cleanPhone(o?.[k]);
      if (val) return val;
    }
  }

  return "";
}

function drawInputRow(doc, x, y, label, value, opts = {}) {
  const {
    labelW = 32,
    rowH = 14,
    fieldPad = 2,
    fieldBg = [245, 245, 245],
    fieldBorder = [220, 220, 220],
    labelColor = [40, 90, 40],
    valueFontSize = 11,
    fieldW = 100,
  } = opts;

  const fieldX = x + labelW;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...labelColor);
  doc.text(S(label), x, y + 9);

  doc.setDrawColor(...fieldBorder);
  doc.setFillColor(...fieldBg);
  doc.rect(fieldX, y + 2, fieldW, rowH - 4, "FD");

  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(valueFontSize);

  const maxTextW = fieldW - fieldPad * 2;
  const lines = wrapN(doc, value, maxTextW, 2);

  doc.text(lines[0] || "-", fieldX + fieldPad, y + 9);
  if (lines[1]) doc.text(lines[1], fieldX + fieldPad, y + 14.2);
}

function measureBlock(doc, w, noteText, bullets, fontSize) {
  const lineH = fontSize * 0.46;
  const bulletGap = 1.4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);

  let h = 0;

  if (noteText) {
    const noteLines = doc.splitTextToSize(noteText, w) || [];
    h += noteLines.length * lineH + 2;
  }

  bullets.forEach((b) => {
    const lines = doc.splitTextToSize(S(b), w - 6) || [];
    h += lines.length * lineH + bulletGap;
  });

  return { h, lineH, bulletGap };
}

function drawNoteAndBullets(doc, x, y, w, availH, noteText, bullets) {
  const MIN = 8;
  let size = 10;

  let meas = measureBlock(doc, w, noteText, bullets, size);
  while (meas.h > availH && size > MIN) {
    size -= 0.5;
    meas = measureBlock(doc, w, noteText, bullets, size);
  }

  const { lineH, bulletGap } = meas;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(size);
  doc.setTextColor(0, 0, 0);

  let cy = y;

  if (noteText) {
    const noteLines = doc.splitTextToSize(noteText, w) || [];
    doc.text(noteLines, x, cy);
    cy += noteLines.length * lineH + 2;
  }

  bullets.forEach((b) => {
    const lines = doc.splitTextToSize(S(b), w - 6) || [];
    doc.text("•", x, cy);
    doc.text(lines, x + 4, cy);
    cy += lines.length * lineH + bulletGap;
  });

  return { usedFontSize: size };
}

function fmtDate(fecha) {
  if (!fecha) return "-";
  const s = String(fecha);
  const iso = s.split("T")[0];
  const [yy, mm, dd] = iso.split("-");
  if (!yy || !mm || !dd) return s;
  return `${dd}/${mm}/${yy}`;
}

function fitBox(boxW, boxH, imgW, imgH) {
  const scale = Math.min(boxW / imgW, boxH / imgH);
  return {
    w: imgW * scale,
    h: imgH * scale,
  };
}

/* ------------------------------ main ------------------------------ */

export async function generarPDFRemitoCampo(remitoId, opts = {}) {
  const resp = await getRemitoById(remitoId);

  if (!resp?.ok) {
    alert(resp?.error || "No se pudo obtener el remito.");
    return;
  }

  const r = resp.remito || {};

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const margin = 12;
  const y = 12;

  const logoUrl = opts.logoUrl || "/SanPancracio.png";

  const empIn = opts.empresa || {};
  const empresa = {
    nombre: empIn.nombre || "SAN PANCRACIO SRL",
    linea1:
      empIn.linea1 ||
      empIn.direccion ||
      "Avda. Peron 1600 - Oficina 29 Piso 1 - Yerba Buena - Tucumán",
    linea2:
      empIn.linea2 ||
      [
        empIn.telefono
          ? String(empIn.telefono).replace(/\n/g, " ").trim()
          : "Tel: 0381-3008829",
        empIn.email || "contacto@sanpancracio.com",
      ]
        .filter(Boolean)
        .join(" • "),
  };

  const logoDataURL = await loadImageAsDataURL(logoUrl);

  drawHeader(doc, y, logoDataURL, empresa, {
    logoAlign: "right",
    showTitle: false,
    showInfo: false,
    showLine: false,
    logoW: 33,
    logoH: 18,
  });

  const green = [40, 90, 40];

  doc.setFont("helvetica", "bold");
  doc.setTextColor(...green);
  doc.setFontSize(24);
  doc.text("INFORMACION", margin, y + 8);
  doc.text("DE SU CARGA", margin, y + 20);

  const blockTop = y + 28;
  const footerSafe = 14;
  const blockH = pageH - blockTop - footerSafe;

  const photoW = 72;
  const gap = 6;

  const photoX = margin;
  const photoY = blockTop;
  const photoH = blockH;

  const panelX = photoX + photoW + gap;
  const panelY = blockTop;
  const panelW = pageW - margin - panelX;
  const panelH = blockH;

  const campoUrl = opts.campoUrl || campoImg;
  const campoDataURL = await loadImageAsDataURL(campoUrl);
  const imgFormat = campoDataURL.startsWith("data:image/png") ? "PNG" : "JPEG";
  const imgProps = doc.getImageProperties(campoDataURL);

  const fitted = fitBox(photoW, photoH, imgProps.width, imgProps.height);
  const drawX = photoX + (photoW - fitted.w) / 2;
  const drawY = photoY;

  doc.addImage(campoDataURL, imgFormat, drawX, drawY, fitted.w, fitted.h);

  doc.setFillColor(234, 226, 214);
  doc.rect(panelX, panelY, panelW, panelH, "F");

  const innerX = panelX + 6;
  let cy = panelY + 6;

  const labelW = 32;
  const fieldW = panelW - 6 - labelW - 8;
  const rowOpts = { labelW, fieldW, rowH: 14 };
  const rowStep = 15;

  const remitoStr = `N° ${S(r.codrem) || "-"}`;
  const fechaStr = fmtDate(r.fecha);
  const clienteStr = r?.cliente?.razonsocial ?? r?.cliente ?? "-";
  const choferStr = r?.chofer?.nombre ?? r?.chofer ?? "-";
  const telStr =
    pickPhone(r?.chofer, r?.telefono, r?.cliente, r?.transporte) || "-";
  const variedadStr = r?.variedad ?? "-";

  const cantidadStr =
    r?.kgRecibido != null
      ? `${fmtNum(r.kgRecibido, 0)}`
      : r?.kgCampo != null
      ? `${fmtNum(r.kgCampo, 0)}`
      : "-";

  const precioNum =
    r?.precioVenta != null && Number(r.precioVenta) !== 0
      ? Number(r.precioVenta)
      : Number(r?.precioCompra || 0);

  const precioStr = precioNum ? `$ ${fmtNum(precioNum)}` : "-";

  const fleteNum = Number(r?.flete || 0);
  const fleteStr = fleteNum > 0 ? `$ ${fmtNum(fleteNum)}` : "";

  drawInputRow(doc, innerX, cy, "REMITO", remitoStr, rowOpts);
  cy += rowStep;

  drawInputRow(doc, innerX, cy, "FECHA", fechaStr, rowOpts);
  cy += rowStep;

  drawInputRow(doc, innerX, cy, "CLIENTE", clienteStr, rowOpts);
  cy += rowStep;

  drawInputRow(doc, innerX, cy, "CHOFER", choferStr, rowOpts);
  cy += rowStep;

  drawInputRow(doc, innerX, cy, "TELÉFONO", telStr, rowOpts);
  cy += rowStep;

  drawInputRow(doc, innerX, cy, "VARIEDAD", variedadStr, rowOpts);
  cy += rowStep;

  drawInputRow(doc, innerX, cy, "CANTIDAD", cantidadStr, rowOpts);
  cy += rowStep;

  drawInputRow(doc, innerX, cy, "PRECIO KG", precioStr, rowOpts);
  cy += 12;

  drawInputRow(doc, innerX, cy, "PRECIO FLETE", fleteStr || " ", rowOpts);
  cy += 12;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...green);
  doc.text("NOTA", innerX, cy + 6);

  const bullets = [
    "Recuerde coordinar la descarga con el chofer",
    "Por favor verificar la cantidad descargada, de existir faltantes el costo sera descontado a la empresa transportista",
    "Cerrar los DTV una vez descargado el camion",
    "De ser rechazada la carga solicitamos cerrar el DTV y colocar en observaciones el motivo de rechazo",
  ];

  const noteText = "";
  const startTextX = innerX + labelW + 4;
  const startTextY = cy + 10;
  const availH = panelY + panelH - startTextY - 6;

  drawNoteAndBullets(
    doc,
    startTextX,
    startTextY,
    fieldW,
    availH,
    noteText,
    bullets
  );

  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text(
    "Documento electrónico generado por el sistema San Pancracio",
    margin,
    pageH - 8
  );

  const filename = `InfoCampo_${S(r.codrem) || remitoId}.pdf`;
  doc.save(filename);
}